// ============================================================================
// BLE connection recovery state machine.
//
// Commercial gyms are hostile RF environments; machine links drop constantly
// (user steps off a rower, walks for water, 2.4GHz congestion). This machine
// keeps the live session alive across transient drops and only surrenders after
// a hard grace window.
//
//   CONNECTED ──drop──> RECONNECTING_SILENT ──recovered──> CONNECTED
//                              │
//                       grace window elapsed
//                              ▼
//                     DISCONNECTED_FALLBACK ──manual resume──> RECONNECTING_SILENT
//
// "Silent" = the UI holds the last-known telemetry frame and shows a subtle
// reconnect chip rather than tearing down the workout. On recovery within the
// window, a gap descriptor is emitted so the ingestion layer can backfill.
// ============================================================================

export type RecoveryState = 'CONNECTED' | 'RECONNECTING_SILENT' | 'DISCONNECTED_FALLBACK'

export type GapDescriptor = {
  droppedAt: number
  recoveredAt: number
  durationMs: number
  /** Backfill is only safe for short drops; long gaps are left as true holes. */
  backfillable: boolean
}

export type ConnectionStateConfig = {
  /** Hard ceiling before surrendering to fallback. Default 30s per spec. */
  graceWindowMs?: number
  /** Reconnect backoff schedule (ms). */
  backoffScheduleMs?: number[]
  /** Max gap eligible for interpolation backfill. */
  maxBackfillMs?: number
}

type StateListener = (state: RecoveryState, detail?: { attempt?: number; gap?: GapDescriptor }) => void

export class ConnectionStateMachine {
  private state: RecoveryState = 'CONNECTED'
  private droppedAt = 0
  private attempt = 0
  private graceTimer: ReturnType<typeof setTimeout> | null = null
  private backoffTimer: ReturnType<typeof setTimeout> | null = null
  private listeners = new Set<StateListener>()

  private readonly graceWindowMs: number
  private readonly backoffScheduleMs: number[]
  private readonly maxBackfillMs: number
  private readonly reconnect: () => Promise<boolean>

  /** `reconnect` is supplied by the owner: attempt one reconnect, true on success. */
  constructor(reconnect: () => Promise<boolean>, config: ConnectionStateConfig = {}) {
    this.reconnect = reconnect
    this.graceWindowMs = config.graceWindowMs ?? 30_000
    this.backoffScheduleMs = config.backoffScheduleMs ?? [500, 1000, 2000, 4000, 6000]
    this.maxBackfillMs = config.maxBackfillMs ?? 10_000
  }

  getState(): RecoveryState {
    return this.state
  }

  onStateChange(listener: StateListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private transition(state: RecoveryState, detail?: { attempt?: number; gap?: GapDescriptor }) {
    this.state = state
    for (const l of this.listeners) l(state, detail)
  }

  /** Call when the GATT server reports an unexpected disconnect. */
  handleDrop(): void {
    if (this.state !== 'CONNECTED') return
    this.droppedAt = Date.now()
    this.attempt = 0
    this.transition('RECONNECTING_SILENT', { attempt: 0 })

    this.graceTimer = setTimeout(() => this.surrender(), this.graceWindowMs)
    void this.attemptReconnectLoop()
  }

  private async attemptReconnectLoop(): Promise<void> {
    while (this.state === 'RECONNECTING_SILENT') {
      const delay = this.backoffScheduleMs[Math.min(this.attempt, this.backoffScheduleMs.length - 1)]
      await sleep(delay)
      if (this.state !== 'RECONNECTING_SILENT') return

      this.attempt += 1
      this.transition('RECONNECTING_SILENT', { attempt: this.attempt })

      let ok: boolean
      try {
        ok = await this.reconnect()
      } catch {
        ok = false
      }
      if (ok) {
        this.recover()
        return
      }
    }
  }

  private recover(): void {
    this.clearTimers()
    const recoveredAt = Date.now()
    const durationMs = recoveredAt - this.droppedAt
    const gap: GapDescriptor = {
      droppedAt: this.droppedAt,
      recoveredAt,
      durationMs,
      backfillable: durationMs <= this.maxBackfillMs,
    }
    this.transition('CONNECTED', { gap })
  }

  private surrender(): void {
    this.clearTimers()
    this.transition('DISCONNECTED_FALLBACK')
  }

  /** User-initiated retry from the fallback state. */
  resume(): void {
    if (this.state !== 'DISCONNECTED_FALLBACK') return
    this.droppedAt = Date.now()
    this.attempt = 0
    this.transition('RECONNECTING_SILENT', { attempt: 0 })
    this.graceTimer = setTimeout(() => this.surrender(), this.graceWindowMs)
    void this.attemptReconnectLoop()
  }

  dispose(): void {
    this.clearTimers()
    this.listeners.clear()
  }

  private clearTimers(): void {
    if (this.graceTimer) clearTimeout(this.graceTimer)
    if (this.backoffTimer) clearTimeout(this.backoffTimer)
    this.graceTimer = null
    this.backoffTimer = null
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
